/**
 * Extract key facts (blood group, allergies, conditions, address) and contacts from
 * emergency profile sections. Matches bilingual field keys from backend FIELD_SUGGESTIONS.
 */
import { ESSENTIAL_CATEGORY, essentialFieldId } from "./fieldTypes";

const ESSENTIAL_SLOTS = [
  {
    id: "bloodGroup",
    labelEn: "Blood group",
    labelHi: "रक्त समूह",
    // "रक्त" alone would also match "उच्च रक्तचाप" (high BP)
    match: (key) => /blood\s*(group|type)|rakt\s*samuh|रक्त\s*समूह|ब्लड\s*ग्रुप/i.test(key),
  },
  {
    id: "allergies",
    labelEn: "Allergies",
    labelHi: "एलर्जी",
    match: (key) => /allerg|एलर्जी/i.test(key),
  },
  {
    id: "condition",
    labelEn: "Medical condition",
    labelHi: "चिकित्सा स्थिति",
    // "medical" and "medication" must not match here: "Medications" would be
    // taken as the condition and the real conditions field would be dropped.
    match: (key) =>
      /condition|chronic|diabetes|मधुमेह|epilepsy|asthma|स्थिति|चिकित्सा|\bheart\b|\bbp\b/i.test(key) &&
      !/blood\s*(group|type)|रक्त\s*समूह|allerg|एलर्जी|doctor|डॉक्टर|hospital|अस्पताल|insurance|बीमा|medication|medicine|दवा/i.test(key),
  },
  {
    id: "address",
    labelEn: "Home address",
    labelHi: "घर का पता",
    // "पता" must stand alone: it also appears inside "अस्पताल" (hospital)
    match: (key) => /address|(^|[\s(])पता($|[\s)])|home|घर/i.test(key),
  },
];

function extractPhone(value) {
  const m = String(value || "").match(/[6-9]\d{9}/);
  return m ? m[0] : null;
}

function flattenItems(sections) {
  const items = [];
  for (const section of sections || []) {
    for (const item of section.items || []) {
      items.push({
        ...item,
        sectionName: section.name,
        category: section.category,
        slotKey: `${section.name}-${item.key}`,
      });
    }
  }
  return items;
}

/**
 * @returns {{
 *   essentials: Array<{ id, labelEn, labelHi, value, filled: boolean }>,
 *   primaryContact: { name: string, number: string } | null,
 *   otherPhones: Array<{ name: string, number: string }>,
 *   detailSections: Array,
 *   usedKeys: Set<string>
 * }}
 */
export function parseEmergencyProfile(sections = []) {
  const allItems = flattenItems(sections);
  const usedKeys = new Set();
  const essentials = ESSENTIAL_SLOTS.map((slot) => {
    const found = allItems.find((item) => slot.match(item.key.toLowerCase()));
    // Fields the owner put in a medical section stay in that list. The hero
    // still reads them from `essentials`; removing them here is what made the
    // public card drop Blood Group and show Medications under the wrong label.
    if (found && String(found.category || "").toLowerCase() !== "medical") {
      usedKeys.add(found.slotKey);
    }
    return {
      id: slot.id,
      labelEn: slot.labelEn,
      labelHi: slot.labelHi,
      value: found?.value?.trim() || "",
      filled: Boolean(found?.value?.trim()),
    };
  });

  const emergencyItems = allItems.filter((i) => i.category === "emergency");
  const phones = [];
  for (const item of emergencyItems) {
    const number = extractPhone(item.value);
    if (number) {
      phones.push({ name: item.key.replace(/\(.*\)/, "").trim(), number });
      usedKeys.add(item.slotKey);
    }
  }

  // Name field paired with phone in emergency section
  const nameItem = emergencyItems.find(
    (i) => /name|नाम|contact/i.test(i.key) && !extractPhone(i.value)
  );
  if (nameItem) usedKeys.add(nameItem.slotKey);

  // The essential section's contact is the primary one; emergency-section phones become extras
  const essentialItems = allItems.filter((i) => i.category === ESSENTIAL_CATEGORY);
  const essentialPhone = essentialItems.find(
    (i) => essentialFieldId(i.key) === "contactPhone" && extractPhone(i.value)
  );
  const essentialName = essentialItems.find((i) => essentialFieldId(i.key) === "contactName");

  let primaryContact = null;
  let otherPhones = phones.slice(1);
  if (essentialPhone) {
    primaryContact = {
      name: essentialName?.value?.trim() || "Emergency Contact",
      number: extractPhone(essentialPhone.value),
    };
    otherPhones = phones.filter((phone) => phone.number !== primaryContact.number);
  } else if (phones[0]) {
    primaryContact = { name: nameItem?.value?.trim() || phones[0].name, number: phones[0].number };
  }

  // Essential Age, Gender, Address, Blood Group and contact are shown in their own
  // places. A copy of the same field in another section is not listed again.
  const essentialFilled = new Set(
    essentialItems
      .filter((item) => item.value?.trim() && essentialFieldId(item.key))
      .map((item) => essentialFieldId(item.key)),
  );
  allItems.forEach((item) => {
    if (item.category === ESSENTIAL_CATEGORY) return;
    const id = essentialFieldId(item.key);
    if (id && essentialFilled.has(id)) usedKeys.add(item.slotKey);
  });

  const detailSections = (sections || [])
    .filter((section) => section.category !== ESSENTIAL_CATEGORY)
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => !usedKeys.has(`${section.name}-${item.key}`)),
    }))
    .filter((s) => s.items.length > 0);

  return { essentials, primaryContact, otherPhones, detailSections, usedKeys };
}

export function saveBystanderReportId(qrId, reportId) {
  if (!qrId || !reportId) return;
  const key = `eqr_reports_${qrId.toUpperCase()}`;
  try {
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    const entry = {
      id: reportId,
      savedAt: new Date().toISOString(),
    };
    const filtered = existing.filter((r) => r.id !== reportId);
    localStorage.setItem(key, JSON.stringify([entry, ...filtered].slice(0, 5)));
  } catch {
    /* ignore storage errors */
  }
}

/** @deprecated use saveBystanderReportId */
export function saveBystanderReport(qrId, reportId) {
  saveBystanderReportId(qrId, reportId);
}

export function getSavedBystanderReports(qrId) {
  if (!qrId) return [];
  try {
    return JSON.parse(localStorage.getItem(`eqr_reports_${qrId.toUpperCase()}`) || "[]");
  } catch {
    return [];
  }
}
